import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import DevicePermission from 'src/utils/DevicePermission';
import microphoneOff from 'src/assets/icons/icon-microphoneOff.svg';
import camera from 'src/assets/icons/icon-camera.svg';
import cameraOff from 'src/assets/icons/icon-cameraOff.svg';
import microphone from 'src/assets/icons/icon-microphone.svg';
import { Logger } from 'src/services';
import { ErrorType } from './constant';

interface PermissionModalProps {
  show: boolean;
  onClose: () => void;
  onPermissionGranted: () => void;
}

const PermissionModal: React.FC<PermissionModalProps> = ({
  show,
  onClose,
  onPermissionGranted,
}) => {
  const [permissionState, setPermissionState] = useState({
    microphone: false,
    camera: false,
  });
  const [errorInfo, setErrorInfo] = useState<string | null>(null); // For displaying error messages

  const checkCurrentPermissions = async (isInitialCheck = false) => {
    try {
      const deviceStatus = await DevicePermission();
      const newMicState = deviceStatus?.hasMicrophone && deviceStatus?.isMicrophoneAlreadyCaptured;
      const newCamState = deviceStatus?.hasWebcam && deviceStatus?.isWebcamAlreadyCaptured;

      setPermissionState({
        microphone: newMicState,
        camera: newCamState,
      });

      // If it's not the initial check and permissions are still denied,
      // ensure an error message related to denial is shown.
      if (!isInitialCheck && !newMicState && !newCamState && errorInfo === null) {
         // This case might be if user revokes permission from browser settings while modal is open
      }

      return { micGranted: newMicState, camGranted: newCamState };
    } catch (error) {
      console.error('Error checking permissions:', error);
      setPermissionState({ microphone: false, camera: false });
      setErrorInfo('Could not verify permission status. Please try again.');

      return { micGranted: false, camGranted: false };
    }
  };

  // Check permission status whenever modal is shown
  useEffect(() => {
    if (show) {
      setErrorInfo(null); // Clear previous errors
      checkCurrentPermissions(true);
    }
  }, [show]);

  const requestPermissions = async () => {
    setErrorInfo(null); // Clear previous errors before a new request

    try {
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      // If getUserMedia succeeds, permissions are granted for this session.

   
      const { micGranted, camGranted } = await checkCurrentPermissions();

      if (micGranted && camGranted) {
        onPermissionGranted();
        onClose();
      } else {

        Logger.warn('getUserMedia succeeded, but subsequent check indicates not all permissions are fully captured or devices are missing.');
        setErrorInfo('Permissions granted, but there might be an issue with device detection. Please ensure your camera and microphone are properly connected and configured.');
        // Update UI to show what IS granted
        setPermissionState({ microphone: micGranted, camera: camGranted });
      }
    } catch (error: any) {
      Logger.error('Error requesting permissions:', error);
      await checkCurrentPermissions(); // Update UI to reflect current state (likely denied)

      switch (error.name) {
        case ErrorType.NOTALLOWEDERROR:
          setErrorInfo(
            'Permission to access camera and/or microphone was denied. Please allow access in your browser settings for this site and try again.'
          );
          break;

        case ErrorType.NOTFOUNDERROR:
          setErrorInfo(
            'No camera and/or microphone found. Please ensure they are connected and enabled.'
          );
          break;

        default:
          setErrorInfo(
            `An error occurred: ${error.message}. Please try again.`
          );
          break;
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      centered={true}
    >
      <Modal.Header>
        <Modal.Title>Camera and Microphone Access Required</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          To participate in the meeting, we need access to your camera and microphone.
          Please allow access when prompted by your browser.
        </p>
        <div className="permission-status">
          <p>
            <img
              src={permissionState.microphone ? microphone : microphoneOff}
              className="audio-icon"
              alt="audio icon"
            />{' '}
            Microphone: {permissionState.microphone ? '✅ Granted' : ' Not granted'}
          </p>
          <p>
            <img
              src={permissionState.camera ? camera : cameraOff}
              className="video-icon"
              alt="video icon"
            />{' '}
            Camera: {permissionState.camera ? '✅ Granted' : ' Not granted'}
          </p>
        </div>
        {errorInfo && (
          <div className="alert alert-danger mt-3" role="alert">
            {errorInfo}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100"> {/* Changed alignment for better UX with error messages */}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={requestPermissions} disabled={permissionState.camera && permissionState.microphone}>
            {permissionState.camera && permissionState.microphone ? 'Permissions Granted' : 'Request Permissions'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PermissionModal;